import { Controller } from 'stimulus';
import gsap from 'gsap';
import toArray from '../helpers/toArray';

class NavigationController extends Controller {
  connect() {
    this.lock = false;
    this.originalNavigationBubbleSize = this.bubbleTarget.offsetWidth;

    this.navigationElementsTimeline = gsap.timeline();

    const items = toArray(this.element.querySelectorAll('[data-animate="list-item"]'));

    // Animate navigation header (logo basically)
    this.navigationElementsTimeline.fromTo(this.element.querySelector('[data-animate="header"]'), {
      opacity: 0,
    }, {
      opacity: 1,
    });

    // Animate the navigation items
    this.navigationElementsTimeline.fromTo(items, {
      opacity: 0,
      y: '100%',
    }, {
      opacity: 1,
      y: 0,
      stagger: 0.1,
      duration: 0.25,
    }, '-=0.2');

    // Animate the navigation social media element
    this.navigationElementsTimeline.fromTo(this.element.querySelector('[data-animate="social"]'), {
      opacity: 0,
      y: '100%',
    }, {
      opacity: 1,
      y: 0,
      duration: 0.25,
    }, '-=0.2');

    const bgElement = this.element.querySelector('[data-animate="bg"]');
    const stripeElements = toArray(bgElement.querySelectorAll('path'));

    bgElement.style.opacity = 1;

    // Animate the lines
    this.navigationElementsTimeline.fromTo(stripeElements, {
      x: '100%',
    }, {
      x: 0,
      stagger: 0.075,
      delay: 0.2,
      duration: 1.5,
    }, '-=2.8');

    this.navigationElementsTimeline.pause();
  }

  async onToggle(event) {
    if (this.lock) {
      return;
    }

    const { currentTarget } = event;
    const isExpanded = currentTarget.getAttribute('aria-expanded') === 'true';
    const navigationTarget = document.getElementById(currentTarget.getAttribute('aria-controls'));

    this.lock = true;

    if (isExpanded) {
      await this.closeNavigation();
      currentTarget.setAttribute('aria-expanded', !isExpanded);
      navigationTarget.setAttribute('aria-hidden', isExpanded);
    } else {
      currentTarget.setAttribute('aria-expanded', !isExpanded);
      navigationTarget.setAttribute('aria-hidden', isExpanded);
      await this.openNavigation();
    }

    this.lock = false;
  }

  closeNavigation() {
    return this.navigationElementsTimeline
      .timeScale(4)
      .reverse()
      .then(() => (
        this.animateBubble(this.originalNavigationBubbleSize, false)
      ))
      .then(() => {
        this.bubbleTarget.removeAttribute('style');
      });
  }

  openNavigation() {
    return this.animateBubble(this.calculateExpectedBubbleSize(), true)
      .then(() => {
        this.navigationElementsTimeline.play();
      });
  }

  animateBubble(size, shouldCenter) {
    const scale = size / this.originalNavigationBubbleSize;
    const initialX = this.bubbleTarget.offsetLeft + (this.originalNavigationBubbleSize / 2);
    const initialY = this.bubbleTarget.offsetTop + (this.originalNavigationBubbleSize / 2);
    const expectedX = window.outerWidth / 2;
    const expectedY = window.outerHeight / 2;

    return gsap.to(this.bubbleTarget, {
      scale,
      duration: 0.25,
      x: shouldCenter ? expectedX - initialX : 0,
      y: shouldCenter ? expectedY - initialY : 0,
    });
  }

  /**
   * Calculate how large the circle should be to cover the whole window.
   *
   * @return number
   */
  // eslint-disable-next-line class-methods-use-this
  calculateExpectedBubbleSize() {
    let { outerWidth, outerHeight } = window;
    const extraSpacing = 10;

    outerWidth += extraSpacing;
    outerHeight += extraSpacing;

    return Math.sqrt((outerWidth * outerWidth) + (outerHeight * outerHeight));
  }
}

NavigationController.targets = [
  'bubble',
];

export default NavigationController;
